import { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Eye, EyeOff, Save, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GATEWAY_ICONS = {
  stripe: '💳',
  paypal: '🅿️',
  paystack: '💵',
  flutterwave: '🦋',
  korapay: '🏦',
  monnify: '💰',
  paysafe: '🔐'
};

const GatewaySettings = () => {
  const { token } = useAuth();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApiKey, setShowApiKey] = useState({});
  const [editData, setEditData] = useState({ api_key: '', secret_key: '', is_enabled: false });

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGateways(response.data);
    } catch (error) {
      console.error('Failed to fetch gateways:', error);
      toast.error('Failed to fetch payment gateways');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (gateway) => {
    setSelectedGateway(gateway);
    setEditData({
      api_key: gateway.api_key || '',
      secret_key: gateway.secret_key || '',
      is_enabled: gateway.is_enabled
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/payment-gateways/${selectedGateway.id}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedGateway.name} settings updated!`);
      setShowEditModal(false);
      fetchGateways();
    } catch (error) {
      toast.error('Failed to update gateway settings');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (gateway) => {
    try {
      await axios.put(
        `${API}/admin/payment-gateways/${gateway.id}`,
        { is_enabled: !gateway.is_enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${gateway.name} ${!gateway.is_enabled ? 'enabled' : 'disabled'}`);
      fetchGateways();
    } catch (error) {
      toast.error('Failed to toggle gateway');
    }
  };

  const toggleShowApiKey = (gatewayId) => {
    setShowApiKey(prev => ({ ...prev, [gatewayId]: !prev[gatewayId] }));
  };

  const maskKey = (key) => {
    if (!key) return 'Not configured';
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
  };

  // Get webhook URL for a gateway - use current origin or production domain
  const getWebhookUrl = (gatewaySlug) => {
    // For admin display, show the production domain if available, otherwise current origin
    const baseUrl = window.location.hostname.includes('pactac.shop') 
      ? 'https://pactac.shop' 
      : window.location.origin;
    const webhookUrls = {
      stripe: `${baseUrl}/api/webhook/stripe`,
      paystack: `${baseUrl}/api/webhook/paystack`,
      flutterwave: `${baseUrl}/api/webhook/flutterwave`,
      paypal: `${baseUrl}/api/webhook/paypal`,
      korapay: `${baseUrl}/api/webhook/korapay`,
      monnify: `${baseUrl}/api/webhook/monnify`,
      paysafe: `${baseUrl}/api/paysafe/webhook`
    };
    return webhookUrls[gatewaySlug] || null;
  };

  // Get callback/return URL for a gateway
  const getCallbackUrl = (gatewaySlug) => {
    const baseUrl = window.location.hostname.includes('pactac.shop') 
      ? 'https://pactac.shop' 
      : window.location.origin;
    return `${baseUrl}/order-complete/{order_id}`;
  };

  // Copy webhook URL to clipboard
  const copyWebhookUrl = async (url) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Webhook URL copied to clipboard!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Webhook URL copied to clipboard!');
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy URL. Please copy manually.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Gateways
          </CardTitle>
          <CardDescription>
            Configure and manage your payment providers. Enable/disable gateways and set API credentials.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {gateways.map((gateway) => (
              <div 
                key={gateway.id} 
                className={`border rounded-lg p-6 transition-all ${
                  gateway.is_enabled 
                    ? 'border-green-200 bg-green-50/30' 
                    : 'border-gray-200 bg-gray-50/30'
                }`}
                data-testid={`gateway-${gateway.slug}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{GATEWAY_ICONS[gateway.slug] || '💳'}</span>
                    <div>
                      <h3 className="font-bold text-lg">{gateway.name}</h3>
                      <p className="text-sm text-gray-500">
                        {gateway.api_key ? 'API Key configured' : 'No API key set'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {gateway.is_enabled ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                        <CheckCircle className="h-4 w-4" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400 text-sm font-medium">
                        <XCircle className="h-4 w-4" />
                        Disabled
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">API Key:</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {showApiKey[gateway.id] ? (gateway.api_key || 'Not set') : maskKey(gateway.api_key)}
                      </code>
                      {gateway.api_key && (
                        <button 
                          onClick={() => toggleShowApiKey(gateway.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {showApiKey[gateway.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Webhook URL */}
                  {getWebhookUrl(gateway.slug) && (
                    <div className="text-sm">
                      <span className="text-gray-600 block mb-1">Webhook URL:</span>
                      <div className="flex items-center gap-2">
                        <code className="bg-blue-50 border border-blue-200 px-2 py-1 rounded text-xs text-blue-800 flex-1 truncate">
                          {getWebhookUrl(gateway.slug)}
                        </code>
                        <button 
                          onClick={() => copyWebhookUrl(getWebhookUrl(gateway.slug))}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium whitespace-nowrap"
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Add this URL in your {gateway.name} dashboard settings
                      </p>
                    </div>
                  )}

                  {/* Callback/Return URL */}
                  <div className="text-sm">
                    <span className="text-gray-600 block mb-1">Callback URL (Return URL):</span>
                    <div className="flex items-center gap-2">
                      <code className="bg-green-50 border border-green-200 px-2 py-1 rounded text-xs text-green-800 flex-1 truncate">
                        {getCallbackUrl(gateway.slug)}
                      </code>
                      <button 
                        onClick={() => copyWebhookUrl(getCallbackUrl(gateway.slug))}
                        className="text-green-600 hover:text-green-800 text-xs font-medium whitespace-nowrap"
                      >
                        Copy
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      User returns here after payment (auto-configured)
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleEdit(gateway)}
                    data-testid={`edit-gateway-${gateway.slug}`}
                  >
                    Configure
                  </Button>
                  <Switch
                    checked={gateway.is_enabled}
                    onCheckedChange={() => handleQuickToggle(gateway)}
                    data-testid={`toggle-gateway-${gateway.slug}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-bold mb-2 text-blue-800">Stripe Setup</h3>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard</a></li>
              <li>Navigate to Developers → API Keys</li>
              <li>Copy your Publishable and Secret keys</li>
              <li>Paste them in the gateway configuration</li>
            </ol>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <h3 className="font-bold mb-2 text-purple-800">Paysafe (Custom Gateway)</h3>
            <p className="text-sm text-purple-700">
              Paysafe uses a manual verification process. Customers upload payment receipts, 
              and admins verify them in the Paysafe tab before orders are processed.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{GATEWAY_ICONS[selectedGateway?.slug] || '💳'}</span>
              Configure {selectedGateway?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Enable Gateway</Label>
                <p className="text-sm text-gray-500">Allow customers to use this payment method</p>
              </div>
              <Switch
                checked={editData.is_enabled}
                onCheckedChange={(checked) => setEditData({ ...editData, is_enabled: checked })}
              />
            </div>

            <div>
              <Label>API Key / Public Key</Label>
              <Input
                value={editData.api_key}
                onChange={(e) => setEditData({ ...editData, api_key: e.target.value })}
                placeholder="pk_test_... or similar"
                className="mt-1 font-mono text-sm"
              />
            </div>

            <div>
              <Label>Secret Key</Label>
              <Input
                type="password"
                value={editData.secret_key}
                onChange={(e) => setEditData({ ...editData, secret_key: e.target.value })}
                placeholder="sk_test_... or similar"
                className="mt-1 font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Keep this secure. Never share your secret key.</p>
            </div>

            {selectedGateway?.slug === 'paysafe' && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Paysafe uses manual verification. No API keys required - 
                  customers upload receipts for admin approval.
                </p>
              </div>
            )}

            {/* Webhook URL Info */}
            {selectedGateway?.slug && getWebhookUrl(selectedGateway.slug) && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <Label className="text-blue-800 font-bold">Webhook URL</Label>
                <p className="text-xs text-blue-700 mt-1 mb-2">
                  Add this URL in your {selectedGateway.name} dashboard to receive payment notifications:
                </p>
                <div className="flex items-center gap-2">
                  <code className="bg-white border border-blue-300 px-2 py-1 rounded text-xs text-blue-900 flex-1 break-all">
                    {getWebhookUrl(selectedGateway.slug)}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyWebhookUrl(getWebhookUrl(selectedGateway.slug))}
                    className="text-blue-600 border-blue-300 text-xs"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}

            {/* Callback URL Info */}
            {selectedGateway?.slug && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <Label className="text-green-800 font-bold">Callback URL (Return URL)</Label>
                <p className="text-xs text-green-700 mt-1 mb-2">
                  User is redirected here after completing payment:
                </p>
                <div className="flex items-center gap-2">
                  <code className="bg-white border border-green-300 px-2 py-1 rounded text-xs text-green-900 flex-1 break-all">
                    {getCallbackUrl(selectedGateway.slug)}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyWebhookUrl(getCallbackUrl(selectedGateway.slug))}
                    className="text-green-600 border-green-300 text-xs"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  ✓ This is auto-configured in your checkout flow
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GatewaySettings;
